import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { canAccessDashboardFromContext } from "../utils/onboardingNavigation";
import OrganizationVerificationModal from "../components/OrganizationVerificationModal";
import { isPoliticianSetupAccount } from "../utils/accountTypes";

function getContent(accountTypeName) {
  if (accountTypeName === "municipality") {
    return {
      title: "You're All Set!",
      description:
        "Your municipality account is ready. We'll notify you once your account has been reviewed and approved.",
      showVerify: false,
      verifyText: "",
    };
  }

  if (
    accountTypeName === "organization" ||
    accountTypeName === "non_profit"
  ) {
    return {
      title: "Almost There!",
      description:
        "One more step to unlock all features for your organization. Verification helps build trust with your community.",
      showVerify: true,
      verifyText: "Verify Your Organization",
    };
  }

  if (isPoliticianSetupAccount(accountTypeName)) {
    return {
      title: "Welcome!",
      description:
        "You're set up to represent your constituents on CivixThread. Verify your identity to earn a verified badge and access additional tools.",
      showVerify: true,
      verifyText: "Verify Your Identity",
    };
  }

  if (accountTypeName === "citizen") {
    return {
      title: "You're Ready!",
      description:
        "Explore legislation, follow your representatives, and take action on the issues that matter to you.",
      showVerify: true,
      verifyText: "Verify Your Identity",
    };
  }

  return {
    title: "You're Ready!",
    description:
      "Your account is all set. Head to the dashboard to get started.",
    showVerify: true,
    verifyText: "Verify Your Identity",
  };
}

export default function FinalSetupPage() {
  const navigate = useNavigate();
  const { accountTypeName, loadUserContext, canAccessDashboard } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [continueError, setContinueError] = useState(null);
  const [showOrgVerification, setShowOrgVerification] = useState(false);

  const isOrgAccount = accountTypeName === "organization" || accountTypeName === "non_profit";
  const content = getContent(accountTypeName);

  const handleContinue = async () => {
    setContinueError(null);
    setIsLoading(true);
    try {
      const ctx = await loadUserContext();
      if (!ctx) {
        setContinueError(
          "We couldn't finish loading your account. Please try again."
        );
        return;
      }
      if (!canAccessDashboardFromContext(ctx) && !canAccessDashboard) {
        setContinueError(
          "Please choose a username before continuing to your dashboard."
        );
        navigate("/username-selection");
        return;
      }
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-start justify-center px-4 pt-32 pb-12">
      <div className="w-full max-w-lg text-center space-y-8">
        <div className="flex justify-center">
          <div className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-14 h-14 text-emerald-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
        </div>

        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-slate-900">
            {content.title}
          </h1>
          <p className="text-lg text-slate-900 leading-relaxed max-w-md mx-auto">
            {content.description}
          </p>
        </div>

        {accountTypeName === "municipality" && (
          <p className="text-sm text-slate-900 italic">
            Your account is pending approval. You'll receive a notification once
            it's been verified.
          </p>
        )}

        {continueError && (
          <p
            className="text-base text-red-800 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
            role="alert"
          >
            {continueError}
          </p>
        )}

        <div className="space-y-4 pt-2">
          {content.showVerify && (
            <button
              type="button"
              onClick={() => isOrgAccount ? setShowOrgVerification(true) : navigate("/verify-identity")}
              className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-[#047857] text-[#047857] font-semibold py-3.5 px-6 text-base hover:bg-emerald-50 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              {content.verifyText}
            </button>
          )}

          <button
            type="button"
            onClick={handleContinue}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#047857] text-white font-semibold py-3.5 px-6 text-base hover:bg-emerald-800 transition-colors disabled:opacity-60"
          >
            {isLoading ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            ) : (
              <>
                Continue to Dashboard
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      <OrganizationVerificationModal
        isOpen={showOrgVerification}
        onClose={() => setShowOrgVerification(false)}
      />
    </div>
  );
}
